import React, { useState } from "react";
import Swal from "sweetalert2";
import {
  invalidateCareerLibraryQueries,
  useAllCareers,
  useProgrammeCareerLinks,
} from "../../query/hooks/QueryHooksCareerLibrary";
import { Link, useHistory } from "react-router-dom";
import { RenderSchool } from "../../utils/CommonGroupingItem";
import { RenderCareerDemandLevel } from "../../utils/CommonStatusItems";
import LinkedItemsTable from "./LinkedItemsTable";
import LinkPicker from "./LinkPicker";
import RowActions from "./RowActions";
import "./careerLibrary.css";
import { HintText } from "./careerLibrary.styles";
import { formatSalaryRange } from "./careerLibraryUtils";
import {
  UpdateProgrammeCareerLinks,
} from "../../services/CareerLibraryServices";

/**
 * The Career Opportunities table on PMC programme and short course pages.
 * Linking and unlinking saves immediately to the shared career_programme_links
 * table. It sits beneath the screen's own Career Opportunities editor, which
 * remains the website's fallback until careers are linked.
 * @param {object} props pmcId and pmcType ('programme' | 'shortcourse')
 */
function CareerPicker({ pmcId, pmcType }) {
  const history = useHistory();
  const [careerPickerOpen, setCareerPickerOpen] = useState(false);

  const careers = useAllCareers().data ?? [];
  const linkedCareers = useProgrammeCareerLinks(pmcId, pmcType).data ?? [];

  /**
   * Persists the full linked set, then reflects the server's result.
   * @param {Array<number>} careerIds The complete set of linked career ids
   */
  const persistLinks = async (careerIds) => {
    try {
      await UpdateProgrammeCareerLinks({
        pmc_id: pmcId,
        pmc_type: pmcType,
        careerIds,
      });
      await invalidateCareerLibraryQueries();
    } catch (error) {
      console.log("CareerPicker.js->persistLinks->Error", error);
      Swal.fire("Error", "The career links could not be saved.", "error");
    }
  };

  if (!pmcId) {
    return <HintText>Save this record first, then link the careers it leads to.</HintText>;
  }

  const linkedIds = linkedCareers.map((linkedCareer) => linkedCareer.id);

  const distinctOptions = (sourceRows, readValue) => {
    const values = sourceRows
      .map(readValue)
      .filter((value) => value !== null && value !== undefined && value !== "");
    return [...new Set(values)].map((value) => ({ value, label: String(value) }));
  };

  const unlinkCareers = (careerIds) =>
    persistLinks(linkedIds.filter((careerId) => !careerIds.includes(careerId)));

  const filtersFor = (sourceRows) => [
    {
      key: "school",
      label: "School",
      options: distinctOptions(sourceRows, (row) => row.school),
      renderLabelFunction: RenderSchool,
      readValue: (row) => row.school,
    },
    {
      key: "demand",
      label: "Demand Level",
      options: distinctOptions(sourceRows, (row) => row.demand_level),
      renderLabelFunction: RenderCareerDemandLevel,
      readValue: (row) => row.demand_level,
    },
  ];

  const columns = [
    {
      name: "Career",
      selector: "name",
      sortable: true,
      grow: 2,
      minWidth: "220px",
      cell: (row) => (
        <Link
          to={`/career-library/careers/open/${row.id}`}
          title={row.name}
          className="career-name-link curser feature-name"
        >
          <span className="textLimit100">{row.name}</span>
        </Link>
      ),
    },
    {
      name: "School",
      selector: "school",
      sortable: true,
      grow: 2,
      minWidth: "240px",
      cell: (row) =>
        row?.school ? (
          <span className="feature-name">
            <span className="textLimit100">{RenderSchool(row.school).html}</span>
          </span>
        ) : (
          "-"
        ),
    },
    {
      name: "Salary Range (Monthly)",
      selector: "salary_entry",
      sortable: true,
      grow: 1,
      minWidth: "180px",
      cell: (row) => <span>{formatSalaryRange(row.salary_entry, row.salary_expert)}</span>,
    },
    {
      name: "Demand Level",
      selector: "demand_level",
      sortable: true,
      grow: 1.5,
      minWidth: "170px",
      cell: (row) => (row?.demand_level ? RenderCareerDemandLevel(row.demand_level).html : "-"),
    },
    {
      name: "Action",
      width: "112px",
      cell: (row) => (
        <RowActions
          onOpen={() => history.push(`/career-library/careers/open/${row.id}`)}
          openTitle="Open Career"
          openScopes={["careerlibrarycareersview"]}
          onDanger={() => unlinkCareers([row.id])}
          dangerTitle="Unlink Career"
          dangerIcon="fal fa-unlink"
          dangerScopes={["pmcpedit"]}
        />
      ),
    },
  ];

  return (
    <div className="form-group-blk">
      <LinkedItemsTable
        className="career-links-table--flush"
        rows={linkedCareers}
        columns={columns}
        searchText={(row) => `${row.name} ${row.school_name || ""}`}
        exportFileName="Linked_Careers"
        exportRow={(fileType, exportRows) =>
          exportRows.map((row) => ({
            Career: row.name,
            School: row.school ? RenderSchool(row.school).text : "-",
            "Salary Range (Monthly)": formatSalaryRange(row.salary_entry, row.salary_expert),
            "Demand Level": row.demand_level || "-",
          }))
        }
        filters={filtersFor(linkedCareers)}
        linkButton={
          <LinkPicker
            key="link-career"
            label="Link Career"
            title="Assign Linked Careers"
            rows={careers}
            linkedKeys={linkedIds}
            rowKey={(row) => row.id}
            columns={columns.filter((column) => column.name !== "Action")}
            searchText={(row) => `${row.name} ${row.school_name || ""}`}
            filters={filtersFor(careers)}
            exportFileName="Careers"
            open={careerPickerOpen}
            onOpenChange={setCareerPickerOpen}
            exportRow={(fileType, exportRows) =>
              exportRows.map((row) => ({
                Career: row.name,
                School: row.school ? RenderSchool(row.school).text : "-",
                "Salary Range (Monthly)": formatSalaryRange(row.salary_entry, row.salary_expert),
                "Demand Level": row.demand_level || "-",
              }))
            }
            onToggle={(careerId, isLinked) =>
              persistLinks(
                isLinked
                  ? linkedIds.filter((linkedId) => linkedId !== careerId)
                  : [...linkedIds, careerId]
              )
            }
          />
        }
      />
    </div>
  );
}

export default CareerPicker;
